<template>
    <!-- Table variant: header row + body rows matching the shared table card -->
    <div
        v-if="variant === 'table'"
        class="overflow-hidden"
        role="status"
        :aria-label="t('common.loading')"
    >
        <div
            class="px-4 py-3 sm:px-6 border-b border-gray-200 dark:border-gray-700 space-y-2"
        >
            <div
                class="h-4 w-1/4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"
            ></div>
        </div>
        <div
            v-for="row in rows"
            :key="row"
            class="px-4 py-3.5 sm:px-6 border-b border-gray-100 dark:border-gray-700/50 flex items-center gap-4"
        >
            <div
                v-for="col in cols"
                :key="col"
                class="h-4 rounded bg-gray-200 dark:bg-gray-700 animate-pulse flex-1"
                :style="{
                    maxWidth: (100 / cols) * (1 + ((row + col) % 3)) + '%',
                }"
            ></div>
        </div>
        <span class="sr-only">{{ t("common.loading") }}</span>
    </div>

    <!-- Cards variant: grid of card skeletons -->
    <div
        v-else
        class="grid gap-6"
        :class="cardGridClass"
        role="status"
        :aria-label="t('common.loading')"
    >
        <div
            v-for="row in rows"
            :key="row"
            class="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 space-y-4"
        >
            <div class="flex items-start justify-between gap-4">
                <div class="space-y-2 flex-1">
                    <div
                        class="h-5 w-2/3 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"
                    ></div>
                    <div
                        class="h-3.5 w-1/2 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"
                    ></div>
                </div>
                <div
                    class="h-6 w-20 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse"
                ></div>
            </div>
            <div class="space-y-2">
                <div
                    class="h-3.5 w-full rounded bg-gray-200 dark:bg-gray-700 animate-pulse"
                ></div>
                <div
                    class="h-3.5 w-4/5 rounded bg-gray-200 dark:bg-gray-700 animate-pulse"
                ></div>
            </div>
            <div
                class="pt-4 border-t border-gray-100 dark:border-gray-700 flex justify-end"
            >
                <div
                    class="h-8 w-24 rounded-md bg-gray-200 dark:bg-gray-700 animate-pulse"
                ></div>
            </div>
        </div>
        <span class="sr-only">{{ t("common.loading") }}</span>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    rows?: number;
    cols?: number;
    variant?: "table" | "cards";
}

const props = withDefaults(defineProps<Props>(), {
    rows: 5,
    cols: 5,
    variant: "table",
});

const { t } = useI18n();

const cardGridClass = computed(() =>
    props.rows <= 2
        ? "grid-cols-1"
        : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
);
</script>
